import type { AasApiAdapter } from "../adapter";
import { isHttpError, type QueryParams, requestJson } from "../http";
import type { Approval, ApprovalStatus } from "../types";
import type { BackendApproval } from "./backendTypes";
import { asObject } from "./shared";

const STATUSES: ApprovalStatus[] = ["pending", "approved", "denied", "expired", "canceled"];

function toStatus(value: string): ApprovalStatus {
	return STATUSES.includes(value as ApprovalStatus) ? (value as ApprovalStatus) : "pending";
}

function toExecution(value: string | null | undefined): Approval["execution"] {
	return value === "executing" || value === "executed" || value === "outcome_unknown"
		? value
		: null;
}

function toRequesterKind(value: string): Approval["requesterKind"] {
	return value === "end_user" || value === "anonymous" || value === "worker" ? value : "member";
}

function toResolvedVia(value: string | null): Approval["resolvedVia"] {
	return value === "console" || value === "conversation" || value === "link" || value === "system"
		? value
		: null;
}

export function mapApproval(row: BackendApproval): Approval {
	return {
		id: row.id,
		kind: row.kind === "mcp_elicitation" ? "mcp_elicitation" : "tool_call",
		conversationId: row.conversationId,
		profileId: row.profileId,
		action: row.action,
		toolCallId: row.toolCallId,
		arguments: asObject(row.arguments),
		editedFrom:
			row.editedFrom === null || row.editedFrom === undefined ? null : asObject(row.editedFrom),
		preview: row.preview,
		note: row.note,
		approvers: Array.isArray(row.approvers)
			? row.approvers.filter((entry): entry is string => typeof entry === "string")
			: [],
		requesterKind: toRequesterKind(row.requesterKind),
		requesterId: row.requesterId,
		status: toStatus(row.status),
		execution: toExecution(row.execution),
		requestedAt: row.requestedAt,
		expiresAt: row.expiresAt,
		resolvedAt: row.resolvedAt,
		approver: row.approver,
		resolvedVia: toResolvedVia(row.resolvedVia),
		resolutionReason: row.resolutionReason,
	};
}

export const approvalsApi: AasApiAdapter["approvals"] = {
	async list(filters = {}) {
		const query: QueryParams = {};
		if (filters.status) query.status = filters.status;
		if (filters.conversationId) query.conversationId = filters.conversationId;
		if (filters.limit) query.limit = String(filters.limit);
		const rows = await requestJson<BackendApproval[]>("/api/approvals", { query });
		return rows.map(mapApproval);
	},
	async get(id) {
		try {
			const row = await requestJson<BackendApproval>(`/api/approvals/${encodeURIComponent(id)}`);
			return mapApproval(row);
		} catch (error) {
			if (isHttpError(error) && error.status === 404) return null;
			throw error;
		}
	},
	async pendingCount() {
		const response = await requestJson<{ count: number }>("/api/approvals/pending-count");
		return response.count;
	},
	async resolve(id, input) {
		const row = await requestJson<BackendApproval>(
			`/api/approvals/${encodeURIComponent(id)}/resolve`,
			{ method: "POST", body: input },
		);
		return mapApproval(row);
	},
};
